import { supabase } from './client';
import type { Lesson, Slide, Session, Submission, Grade } from './client';
import { projectId, publicAnonKey } from './info';

// ============================================================
// OPTIMIZED QUERY FUNCTIONS
// Performance-focused with minimal database round trips
// ============================================================

// ============================================================
// LESSON QUERIES
// ============================================================

/**
 * Get lessons for teacher (optimized - minimal columns)
 * Pagination supported for better performance
 */
export async function getTeacherLessons(
  teacherId: string,
  options: {
    limit?: number;
    offset?: number;
    status?: 'draft' | 'published';
  } = {}
) {
  const { limit = 20, offset = 0, status } = options;

  let query = supabase
    .from('lessons')
    .select('id, title, description, mode, status, cover_image, created_at, updated_at', { count: 'exact' })
    .eq('teacher_id', teacherId)
    .order('updated_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  return { lessons: data || [], count: count || 0 };
}

/**
 * Get all lessons for teacher (without pagination) - for dropdowns/selects
 * Includes both owned lessons and lessons shared with the teacher
 */
export async function getLessons(teacherId: string) {
  // Get owned lessons
  const { data: ownedLessons, error: ownedError } = await supabase
    .from('lessons')
    .select('id, title, description, mode, status, cover_image, created_at, updated_at, teacher_id')
    .eq('teacher_id', teacherId);

  if (ownedError) throw ownedError;

  // Get shared lessons
  const { data: sharedLessonsData, error: sharedError } = await supabase
    .from('lesson_shares')
    .select(`
      lessons:lesson_id (
        id,
        title,
        description,
        mode,
        status,
        cover_image,
        created_at,
        updated_at,
        teacher_id
      )
    `)
    .eq('shared_to_teacher_id', teacherId);

  if (sharedError) throw sharedError;

  // Extract lessons from the nested structure and add is_shared flag
  const sharedLessons = (sharedLessonsData || [])
    .map(item => item.lessons)
    .filter(lesson => lesson !== null) as any[];

  // Combine and mark shared lessons
  const ownedWithFlag = (ownedLessons || []).map(lesson => ({ ...lesson, is_shared: false }));
  const sharedWithFlag = sharedLessons.map(lesson => ({ ...lesson, is_shared: true }));

  // Merge and sort by updated_at
  const allLessons = [...ownedWithFlag, ...sharedWithFlag]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  return allLessons;
}

/**
 * Get single lesson with full details
 */
export async function getLesson(lessonId: string) {
  const { data, error } = await supabase
    .from('lessons')
    .select(`
      id,
      teacher_id,
      classroom_id,
      title,
      description,
      mode,
      status,
      cover_image,
      created_at,
      updated_at
    `)
    .eq('id', lessonId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Create new lesson
 */
export async function createLesson(lesson: {
  teacher_id: string;
  title: string;
  description?: string;
  mode: 'live' | 'student-paced';
  classroom_id?: string;
  cover_image?: string | null;
}) {
  const { data, error } = await supabase
    .from('lessons')
    .insert({
      ...lesson,
      status: 'draft'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Duplicate a lesson's slides into a new draft owned by the current teacher.
 */
export async function duplicateLessonContent(lessonId: string, teacherId: string) {
  const [sourceLesson, sourceSlides] = await Promise.all([
    getLesson(lessonId),
    getLessonSlides(lessonId)
  ]);

  const duplicatedLesson = await createLesson({
    teacher_id: teacherId,
    title: `Copy of ${sourceLesson.title}`,
    description: sourceLesson.description || '',
    mode: sourceLesson.mode,
    classroom_id: undefined,
    cover_image: sourceLesson.cover_image
  });

  if (sourceSlides.length > 0) {
    const { error } = await supabase
      .from('slides')
      .insert(sourceSlides.map(({ type, content, slide_order }) => ({
        lesson_id: duplicatedLesson.id,
        type,
        content,
        slide_order
      })));

    if (error) {
      await supabase.from('lessons').delete().eq('id', duplicatedLesson.id);
      throw error;
    }
  }

  return duplicatedLesson;
}

/**
 * Update lesson
 */
export async function updateLesson(
  lessonId: string,
  updates: Partial<Omit<Lesson, 'id' | 'teacher_id' | 'created_at' | 'updated_at'>>
) {
  const { data, error } = await supabase
    .from('lessons')
    .update(updates)
    .eq('id', lessonId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete lesson
 */
export async function deleteLesson(lessonId: string) {
  const { error } = await supabase
    .from('lessons')
    .delete()
    .eq('id', lessonId);

  if (error) throw error;
}

/**
 * Delete all shares for a lesson
 */
export async function deleteLessonShares(lessonId: string) {
  const { error } = await supabase
    .from('lesson_shares')
    .delete()
    .eq('lesson_id', lessonId);

  if (error) throw error;
}

// ============================================================
// LESSON SHARING QUERIES
// ============================================================

/**
 * Get lessons shared with a teacher (including their own)
 * Returns both owned and shared lessons
 */
export async function getTeacherLessonsIncludingShared(
  teacherId: string,
  options: {
    limit?: number;
    offset?: number;
    status?: 'draft' | 'published';
  } = {}
) {
  const { limit = 20, offset = 0, status } = options;

  // Get lessons where teacher is owner
  let ownQuery = supabase
    .from('lessons')
    .select('id, title, description, mode, status, cover_image, created_at, updated_at, teacher_id', { count: 'exact' })
    .eq('teacher_id', teacherId);

  if (status) {
    ownQuery = ownQuery.eq('status', status);
  }

  const { data: ownLessons, error: ownError, count: ownCount } = await ownQuery
    .order('updated_at', { ascending: false });

  if (ownError) throw ownError;

  // Get lessons shared with the teacher
  const { data: shares, error: shareError } = await supabase
    .from('lesson_shares')
    .select(
      `lesson_id, 
      lessons!lesson_id(id, title, description, mode, status, cover_image, created_at, updated_at, teacher_id),
       shared_by_teacher_id`
    )
    .eq('shared_to_teacher_id', teacherId)
    .order('created_at', { ascending: false });

  if (shareError) throw shareError;

  // Transform shared lessons
  const sharedLessons = shares?.map((share: any) => ({
    ...share.lessons,
    is_shared: true,
    shared_by: share.shared_by_teacher_id,
  })) || [];

  // Mark own lessons
  const ownLessonsMarked = ownLessons?.map((lesson: any) => ({
    ...lesson,
    is_shared: false,
    shared_by: null,
  })) || [];

  // Combine and deduplicate (in case a lesson is both owned and shared)
  const combined = [...ownLessonsMarked, ...sharedLessons];
  const unique = Array.from(
    new Map(combined.map((item: any) => [item.id, item])).values()
  ) as any[];

  // Apply pagination on combined results
  const paginated = unique
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(offset, offset + limit);

  return {
    lessons: paginated,
    count: unique.length
  };
}

/**
 * Share a lesson with another teacher.
 * Both lesson owners and teachers who have the lesson shared with them can share it.
 */
export async function shareLesson(
  lessonId: string,
  sharedByTeacherId: string,
  teacherToShareWith: string
) {
  // Verify the user has access to this lesson (owner or shared-with)
  const access = await hasLessonAccess(lessonId, sharedByTeacherId);
  if (!access.hasAccess) {
    throw new Error('You can only share lessons you have access to');
  }

  const { data, error } = await supabase
    .from('lesson_shares')
    .insert({
      lesson_id: lessonId,
      shared_by_teacher_id: sharedByTeacherId,
      shared_to_teacher_id: teacherToShareWith,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Revoke lesson share
 */
export async function revokeShareLesson(shareId: string, teacherId: string) {
  // Verify the user owns the share (i.e., they're the one who shared it)
  const { data: share, error: dataError } = await supabase
    .from('lesson_shares')
    .select('id, shared_by_teacher_id')
    .eq('id', shareId)
    .single();

  if (dataError || !share || share.shared_by_teacher_id !== teacherId) {
    throw new Error('You can only revoke shares you created');
  }

  const { error } = await supabase
    .from('lesson_shares')
    .delete()
    .eq('id', shareId);

  if (error) throw error;
}

/**
 * Get all shares for a lesson (teachers it's shared with)
 */
export async function getLessonShares(lessonId: string) {
  const { data, error } = await supabase
    .from('lesson_shares')
    .select(
      `id,
       lesson_id,
       shared_to_teacher_id,
       shared_to_profile:shared_to_teacher_id(id, full_name),
       created_at`
    )
    .eq('lesson_id', lessonId);

  if (error) throw error;
  return data || [];
}

/**
 * Check if a user has access to a lesson (owner or shared)
 */
export async function hasLessonAccess(
  lessonId: string,
  teacherId: string
): Promise<{
  hasAccess: boolean;
  isOwner: boolean;
  isShared: boolean;
}> {
  // Get lesson
  const { data: lesson, error: lessonError } = await supabase
    .from('lessons')
    .select('id, teacher_id')
    .eq('id', lessonId)
    .single();

  if (lessonError || !lesson) {
    return { hasAccess: false, isOwner: false, isShared: false };
  }

  const isOwner = lesson.teacher_id === teacherId;
  if (isOwner) return { hasAccess: true, isOwner: true, isShared: false };

  // Check if shared
  const { data: share } = await supabase
    .from('lesson_shares')
    .select('id')
    .eq('lesson_id', lessonId)
    .eq('shared_to_teacher_id', teacherId)
    .maybeSingle();

  const isShared = !!share;
  return { hasAccess: isShared, isOwner: false, isShared };
}

/**
 * Search for teachers by name (excluding current user and already-shared teachers)
 */
/**
 * Search teachers by name or email
 * Supports both name and email search (useful for sharing lessons)
 * Has built-in fallback for teachers with missing emails
 */
export async function searchTeachers(
  searchQuery: string,
  excludeTeacherId?: string,
  lessonId?: string
) {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return [];
  }

  const query = searchQuery.trim().toLowerCase();
  const isEmail = query.includes('@');

  try {
    // Always fetch all teachers first to do comprehensive filtering
    const { data: allTeachers, error: fetchError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('role', 'teacher')
      .limit(100);

    if (fetchError) {
      console.error('Error fetching teachers:', fetchError);
      throw fetchError;
    }

    let results = allTeachers || [];

    // Filter by search query - search both email AND name fields for better matching
    results = results.filter((teacher: any) => {
      const nameMatch = teacher.full_name?.toLowerCase().includes(query);
      const emailMatch = teacher.email?.toLowerCase().includes(query);
      
      // If searching by email format, prioritize email match but also check name as fallback
      if (isEmail) {
        return emailMatch || nameMatch;
      }
      
      // For non-email queries, check both fields
      return nameMatch || emailMatch;
    });

    // Filter out current user
    if (excludeTeacherId) {
      results = results.filter((t: any) => t.id !== excludeTeacherId);
    }

    // Filter out already-shared teachers if lesson is provided
    if (lessonId) {
      const { data: shares } = await supabase
        .from('lesson_shares')
        .select('shared_to_teacher_id')
        .eq('lesson_id', lessonId);

      const sharedIds = new Set((shares || []).map((s: any) => s.shared_to_teacher_id));
      results = results.filter((t: any) => !sharedIds.has(t.id));
    }

    return results;
  } catch (error) {
    console.error('Error in searchTeachers:', error);
    throw error;
  }
}

/**
 * Get all teachers (for debugging or when you need all available teachers)
 */
export async function getAllTeachers() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .eq('role', 'teacher')
    .order('full_name', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Get teacher by email address for direct sharing
 */
export async function getTeacherByEmail(email: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .eq('role', 'teacher')
    .ilike('email', email)
    .single();

  if (error) throw error;
  return data;
}

// ============================================================
// SLIDE QUERIES
// ============================================================

/**
 * Get slides for a lesson (ordered by slide_order)
 */
export async function getLessonSlides(lessonId: string): Promise<Slide[]> {
  const { data, error } = await supabase
    .from('slides')
    .select('id, lesson_id, type, content, slide_order, created_at')
    .eq('lesson_id', lessonId)
    .order('slide_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Get single slide
 */
export async function getSlide(slideId: string) {
  const { data, error } = await supabase
    .from('slides')
    .select('*')
    .eq('id', slideId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Create slide
 */
export async function createSlide(slide: {
  lesson_id: string;
  type: Slide['type'];
  content: Record<string, any>;
  slide_order: number;
}) {
  const { data, error } = await supabase
    .from('slides')
    .insert(slide)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update slide
 */
export async function updateSlide(
  slideId: string,
  updates: Partial<Omit<Slide, 'id' | 'lesson_id' | 'created_at'>>
) {
  const { data, error } = await supabase
    .from('slides')
    .update(updates)
    .eq('id', slideId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete slide
 */
export async function deleteSlide(slideId: string) {
  const { error } = await supabase
    .from('slides')
    .delete()
    .eq('id', slideId);

  if (error) throw error;
}

/**
 * Reorder slides
 */
export async function reorderSlides(updates: { id: string; slide_order: number }[]) {
  const promises = updates.map(({ id, slide_order }) =>
    supabase
      .from('slides')
      .update({ slide_order })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  
  const errors = results.filter(r => r.error);
  if (errors.length > 0) throw errors[0].error;
}

// ============================================================
// SESSION QUERIES
// ============================================================

/**
 * Auto-end expired student-paced sessions
 */
async function endExpiredStudentPacedSessions() {
  const nowIso = new Date().toISOString();

  const { error } = await supabase
    .from('sessions')
    .update({
      status: 'ended',
      ended_at: nowIso
    })
    .eq('status', 'active')
    .eq('mode', 'student-paced')
    .not('due_date', 'is', null)
    .lt('due_date', nowIso);

  if (error) {
    console.error('Error auto-ending student-paced sessions:', error);
  }
}

/**
 * Get active sessions for teacher
 */
export async function getTeacherActiveSessions(teacherId: string) {
  await endExpiredStudentPacedSessions();

  const { data, error } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      session_code,
      mode,
      current_slide,
      status,
      created_at,
      due_date,
      lessons (
        id,
        title
      )
    `)
    .eq('teacher_id', teacherId)
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Get all sessions for a teacher
 */
export async function getTeacherSessions(teacherId: string): Promise<Session[]> {
  // Get sessions from owned lessons
  const { data: ownedSessions, error: ownedError } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      teacher_id,
      session_code,
      mode,
      status,
      current_slide,
      created_at,
      ended_at,
      lessons!inner (
        id,
        title,
        teacher_id
      )
    `)
    .eq('lessons.teacher_id', teacherId);

  if (ownedError) throw ownedError;

  // Get shared lesson IDs for this teacher
  const { data: sharedLessons, error: sharedError } = await supabase
    .from('lesson_shares')
    .select('lesson_id')
    .eq('shared_to_teacher_id', teacherId);

  if (sharedError) throw sharedError;

  const sharedLessonIds = (sharedLessons || []).map(item => item.lesson_id);

  // Get sessions from shared lessons
  let sharedSessions: any[] = [];
  if (sharedLessonIds.length > 0) {
    const { data: sharedSessionsData, error: sharedSessionsError } = await supabase
      .from('sessions')
      .select(`
        id,
        lesson_id,
        teacher_id,
        session_code,
        mode,
        status,
        current_slide,
        created_at,
        ended_at,
        lessons!inner (
          id,
          title,
          teacher_id
        )
      `)
      .in('lesson_id', sharedLessonIds);

    if (sharedSessionsError) throw sharedSessionsError;
    sharedSessions = sharedSessionsData || [];
  }

  // Combine and sort by created_at
  const allSessions = [...(ownedSessions || []), ...sharedSessions]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return allSessions;
}

/**
 * Get all sessions for a teacher (alias for consistency)
 */
export async function getSessions(teacherId: string) {
  return getTeacherSessions(teacherId);
}

/**
 * Get session by ID with lesson details
 */
export async function getSession(sessionId: string) {
  await endExpiredStudentPacedSessions();

  const { data, error } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      teacher_id,
      session_code,
      mode,
      current_slide,
      status,
      created_at,
      due_date,
      ended_at,
      lessons (
        id,
        title,
        description,
        mode
      )
    `)
    .eq('id', sessionId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching session:', error);
    throw error;
  }
  
  if (!data) {
    throw new Error('Session not found');
  }
  
  return data;
}

/**
 * Get session by code (for students joining)
 * Uses the public RLS policy that allows reading by session_code
 */
export async function getSessionByCode(sessionCode: string) {
  await endExpiredStudentPacedSessions();

  const { data, error } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      teacher_id,
      session_code,
      mode,
      current_slide,
      status,
      created_at,
      due_date,
      ended_at,
      lessons (
        id,
        title,
        description,
        mode
      )
    `)
    .eq('session_code', sessionCode.toUpperCase())
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    console.error('Error fetching session by code:', error);
    throw error;
  }
  
  if (!data) {
    throw new Error('Session not found or not active');
  }
  
  return data;
}

/**
 * Get session by code (for student dashboard access)
 * Includes ended sessions so students can review grades.
 */
export async function getSessionByCodeAnyStatus(sessionCode: string) {
  await endExpiredStudentPacedSessions();

  const { data, error } = await supabase
    .from('sessions')
    .select(`
      id,
      lesson_id,
      teacher_id,
      session_code,
      mode,
      current_slide,
      status,
      created_at,
      due_date,
      ended_at,
      lessons (
        id,
        title,
        description,
        mode
      )
    `)
    .eq('session_code', sessionCode.toUpperCase())
    .maybeSingle();

  if (error) {
    console.error('Error fetching session by code (any status):', error);
    throw error;
  }

  if (!data) {
    throw new Error('Session not found');
  }

  return data;
}

/**
 * Create session
 */
export async function createSession(session: {
  lesson_id: string;
  teacher_id: string;
  mode: 'live' | 'student-paced';
  session_code?: string; // Optional - if not provided, will be generated
  due_date?: Date | string;
}) {
  let sessionCode = session.session_code;
  const dueDate = session.due_date ? new Date(session.due_date).toISOString() : null;

  // Only generate code if not provided
  if (!sessionCode) {
    const { data: codeData, error: codeError } = await supabase
      .rpc('generate_session_code');

    if (codeError) {
      console.error('Error generating session code:', codeError);
      throw codeError;
    }

    sessionCode = codeData;
  }

  console.log('Using session code:', sessionCode);

  const { data, error } = await supabase
    .from('sessions')
    .insert({
      lesson_id: session.lesson_id,
      teacher_id: session.teacher_id,
      mode: session.mode,
      session_code: sessionCode,
      status: 'active',
      current_slide: 0,
      due_date: dueDate
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating session:', error);
    throw error;
  }

  console.log('Created session:', data);
  return data;
}

/**
 * Update session current slide (for live mode)
 */
export async function updateSessionSlide(sessionId: string, slideNumber: number) {
  const { data, error } = await supabase
    .from('sessions')
    .update({ current_slide: slideNumber })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * End session
 */
export async function endSession(sessionId: string) {
  const { data, error } = await supabase
    .from('sessions')
    .update({
      status: 'ended',
      ended_at: new Date().toISOString()
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update session status
 */
export async function updateSession(
  sessionId: string,
  updates: Partial<Session>
) {
  const { data, error } = await supabase
    .from('sessions')
    .update(updates)
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================
// SESSION PARTICIPANT QUERIES
// ============================================================

/**
 * Get participants for a session
 */
export async function getSessionParticipants(sessionId: string) {
  const { data, error } = await supabase
    .from('session_participants')
    .select('id, student_name, joined_at, progress_percentage, last_active')
    .eq('session_id', sessionId)
    .order('joined_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Update participant progress
 */
export async function updateParticipantProgress(
  sessionId: string,
  studentName: string,
  progressPercentage: number
) {
  // Update session_participants
  const { data, error } = await supabase
    .from('session_participants')
    .update({
      progress_percentage: progressPercentage,
      last_active: new Date().toISOString()
    })
    .eq('session_id', sessionId)
    .eq('student_name', studentName)
    .select()
    .single();

  if (error) throw error;

  // Also update grade_summary if it exists
  try {
    await supabase
      .from('grade_summary')
      .update({
        completion_rate: progressPercentage,
        last_activity_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('session_id', sessionId)
      .eq('student_name', studentName);
  } catch (err) {
    // Silently fail if grade_summary doesn't exist or update fails
    console.log('Note: grade_summary not yet available, will be created when migrations are applied');
  }

  return data;
}

export type SessionSecurityIncidentType = 'tab-switch' | 'screenshot' | 'app-switch';

export async function logSessionSecurityIncident(incident: {
  sessionId: string;
  studentName: string;
  incidentType: SessionSecurityIncidentType;
  slideId?: string | null;
  metadata?: Record<string, any>;
}) {
  const { error } = await supabase
    .from('session_security_incidents')
    .insert({
      session_id: incident.sessionId,
      slide_id: incident.slideId ?? null,
      student_name: incident.studentName,
      incident_type: incident.incidentType,
      metadata: incident.metadata ?? {}
    });

  if (error) throw error;
}

// ============================================================
// SUBMISSION QUERIES
// ============================================================

/**
 * Get submissions for a session (paginated)
 */
export async function getSessionSubmissions(
  sessionId: string,
  options: { limit?: number; offset?: number } = {}
) {
  const { limit = 50, offset = 0 } = options;

  const { data, error, count } = await supabase
    .from('submissions')
    .select('id, slide_id, student_name, answer, score, graded, created_at', {
      count: 'exact'
    })
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return { submissions: data || [], count: count || 0 };
}

/**
 * Get submissions for specific slide
 */
export async function getSlideSubmissions(sessionId: string, slideId: string) {
  const { data, error } = await supabase
    .from('submissions')
    .select('id, student_name, answer, score, graded, created_at')
    .eq('session_id', sessionId)
    .eq('slide_id', slideId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('❌ getSlideSubmissions error:', { sessionId, slideId, error, errorCode: error.code, errorMessage: error.message });
    throw error;
  }
  
  console.log('✅ getSlideSubmissions returned:', { 
    sessionId, 
    slideId, 
    count: data?.length || 0,
    data: data?.map(d => ({ id: d.id, student_name: d.student_name, answer: d.answer })) 
  });
  
  return data || [];
}

/**
 * Get student's submission for a slide
 */
export async function getStudentSubmission(
  sessionId: string,
  slideId: string,
  studentName: string
) {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .eq('session_id', sessionId)
    .eq('slide_id', slideId)
    .eq('student_name', studentName)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Create or update submission
 */
export async function upsertSubmission(submission: {
  session_id: string;
  slide_id: string;
  student_name: string;
  answer: Record<string, any>;
}) {
  // Check if submission exists
  const existing = await getStudentSubmission(
    submission.session_id,
    submission.slide_id,
    submission.student_name
  );

  if (existing && !existing.graded) {
    // Update existing ungraded submission
    const { data, error } = await supabase
      .from('submissions')
      .update({ answer: submission.answer })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else if (!existing) {
    // Create new submission
    const { data, error } = await supabase
      .from('submissions')
      .insert(submission)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // If already graded, return existing
  return existing;
}

/**
 * Submit answer (wrapper for serverUpsertSubmission with auto-grading)
 */
export async function submitAnswer(submission: {
  session_id: string;
  slide_id: string;
  student_name: string;
  answer_data: Record<string, any>;
  is_correct?: boolean | null;
  maxPoints?: number;
  calculatedScore?: number;
}) {
  // Check if submission already exists
  const existing = await getStudentSubmission(
    submission.session_id,
    submission.slide_id,
    submission.student_name
  );

  const graded = submission.calculatedScore !== undefined || (
    submission.is_correct !== null && submission.is_correct !== undefined
  );
  const maxPts = submission.maxPoints ?? 10;
  // Use calculatedScore when provided (e.g. partial credit), otherwise boolean-based
  const score = submission.calculatedScore !== undefined
    ? Math.min(submission.calculatedScore, maxPts)
    : (submission.is_correct ? maxPts : 0);

  // Use server endpoint to bypass RLS
  const result = await serverUpsertSubmission({
    id: existing?.id,
    session_id: submission.session_id,
    slide_id: submission.slide_id,
    student_name: submission.student_name,
    answer: submission.answer_data,
    graded,
    score
  });

  try {
    const autoGraded = await serverAutoGradeSubmission(result.id);
    return autoGraded || result;
  } catch (error) {
    console.error('Submission was saved, but auto-grading failed:', error);
    return result;
  }
}

// ============================================================
// GRADE QUERIES
// ============================================================

/**
 * Get grades for a session
 */
export async function getSessionGrades(sessionId: string) {
  const { data, error } = await supabase
    .from('grades')
    .select('*')
    .eq('session_id', sessionId)
    .order('total_score', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Get student's grade
 */
export async function getStudentGrade(sessionId: string, studentName: string) {
  const { data, error } = await supabase
    .from('grades')
    .select('*')
    .eq('session_id', sessionId)
    .eq('student_name', studentName)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Upsert grade (create or update)
 */
export async function upsertGrade(grade: {
  session_id: string;
  lesson_id: string;
  student_name: string;
  total_score: number;
  max_possible_score: number;
  completion_percentage: number;
}) {
  const { error: upsertError } = await supabase
    .from('grades')
    .upsert(grade);

  if (upsertError) throw upsertError;

  const { data, error } = await supabase
    .from('grades')
    .select('*')
    .eq('session_id', grade.session_id)
    .eq('student_name', grade.student_name)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ============================================================
// CLASSROOM QUERIES
// ============================================================

/**
 * Get classrooms for teacher
 */
export async function getTeacherClassrooms(teacherId: string) {
  const { data, error } = await supabase
    .from('classrooms')
    .select('id, title, description, created_at')
    .eq('teacher_id', teacherId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Create classroom
 */
export async function createClassroom(classroom: {
  teacher_id: string;
  title: string;
  description?: string;
}) {
  const { data, error } = await supabase
    .from('classrooms')
    .insert(classroom)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================
// SERVER-SIDE STUDENT SUBMISSION HELPERS
// Route through edge function to bypass RLS for student operations
// ============================================================

const SERVER_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-9d7d881a`;

/**
 * Upsert a student submission via server (bypasses RLS)
 */
export async function serverUpsertSubmission(submission: {
  id?: string;
  session_id: string;
  slide_id: string;
  student_name: string;
  answer: Record<string, any>;
  graded?: boolean;
  score?: number;
}): Promise<{ id: string }> {
  const response = await fetch(`${SERVER_BASE}/student/submission`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
    },
    body: JSON.stringify(submission),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Server submission failed (${response.status})`);
  }

  const result = await response.json();
  return result.submission;
}

export async function serverAutoGradeSubmission(submissionId: string) {
  const response = await fetch(`${SERVER_BASE}/grades/auto-grade`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
    },
    body: JSON.stringify({ submissionId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Server auto-grading failed (${response.status})`);
  }

  const result = await response.json();
  return result.result;
}

/**
 * Get slide submissions via server (bypasses RLS)
 */
export async function serverGetSlideSubmissions(sessionId: string, slideId: string) {
  const response = await fetch(
    `${SERVER_BASE}/student/submissions/${sessionId}/${slideId}`,
    {
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
      },
    }
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to fetch submissions (${response.status})`);
  }

  const result = await response.json();
  return result.submissions || [];
}